import { useSeo, breadcrumbSchema, articleSchema, faqSchema } from "@/lib/seo";
import { useParams, Link } from "wouter";
import { getCanonicalBlogSlug, getPostBySlug } from "@/data/blog";
import { formatBlogDate } from "@/data/pageDates";
import NotFound from "./not-found";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Redirect } from "@/components/Redirect";
import { Fragment } from "react";
import { articleServiceLinks } from "@/data/articleServiceLinks";
import { trackArticleCta } from "@/lib/analytics";

export default function BlogPost() {
  const params = useParams<{ slug: string }>();
  const canonicalSlug = getCanonicalBlogSlug(params.slug);
  const post = canonicalSlug ? getPostBySlug(canonicalSlug) : undefined;
  const serviceLink = post ? articleServiceLinks[post.slug] : undefined;
  const firstParagraph = post?.body.findIndex(section => section.type === "paragraph");

  const faqSection = post?.body.find((s) => s.type === "faq" && s.qa && s.qa.length > 0);

  const crumbs = post
    ? [
        { name: "Home", path: "/" },
        { name: "Insights", path: "/insights" },
        { name: post.title, path: `/insights/${post.slug}` },
      ]
    : [];

  useSeo({
    title: post?.seoTitle ?? post?.title ?? "Insight",
    description: post?.seoDescription ?? post?.excerpt ?? "Automotive marketing insight from Relevant Dealer.",
    keywords: post?.keywords,
    ogImage: post?.heroImage,
    ogType: "article",
    canonicalPath: `/insights/${canonicalSlug ?? params.slug}`,
    publishedDate: post?.publishedDate,
    modifiedDate: post?.publishedDate,
    noIndex: !post,
    jsonLd: post
      ? [
          breadcrumbSchema(crumbs),
          articleSchema({
            title: post.seoTitle ?? post.title,
            description: post.seoDescription ?? post.excerpt,
            slug: post.slug,
            datePublished: post.publishedDate,
            image: post.heroImage,
            category: post.category,
            keywords: post.keywords,
            author: post.author ?? "Steven Laureys",
          }),
          ...(faqSection?.qa ? [faqSchema(faqSection.qa)] : []),
        ]
      : undefined,
  });

  if (!post) {
    return <NotFound />;
  }
  if (canonicalSlug !== params.slug) {
    return <Redirect to={`/insights/${canonicalSlug}`} />;
  }

  return (
    <div className="pt-24 min-h-screen bg-background">
      <article className="container mx-auto px-4 max-w-3xl py-12">
        <Breadcrumbs items={crumbs} />

        <header className="mb-12">
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <span className="bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
              {post.category}
            </span>
            <time dateTime={post.publishedDate} className="text-sm text-muted-foreground font-medium">
              {formatBlogDate(post.publishedDate, "long")}
            </time>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-tight mb-6">
            {post.title}
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed mb-6">
            {post.excerpt}
          </p>
          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
              SL
            </div>
            <div className="text-sm">
              <div className="font-semibold text-foreground">By {post.author ?? "Steven Laureys"}</div>
              <div className="text-muted-foreground">Fractional CMO, Relevant Dealer</div>
            </div>
          </div>
        </header>

        <img 
          src={post.heroImage} 
          alt={post.title} 
          className="w-full aspect-video object-cover rounded-2xl mb-12 shadow-sm border border-border"
        />

        <div className="prose prose-lg prose-headings:font-display prose-headings:font-bold prose-h2:text-3xl prose-a:text-primary max-w-none text-foreground/90">
          {post.body.map((section, idx) => {
            if (section.type === 'paragraph') {
              return <Fragment key={idx}>
                <p>{section.content}</p>
                {serviceLink && idx === firstParagraph && (
                  <p>{serviceLink.context} <Link href={serviceLink.href} onClick={() => trackArticleCta(post.slug, serviceLink.href, "inline")}>{serviceLink.linkText}</Link>{post.slug === "dealer-marketing-capacity-before-campaign-calendar" ? " can help connect those priorities." : "."}</p>
                )}
              </Fragment>;
            }
            if (section.type === 'heading') {
              return <h2 key={idx}>{section.content}</h2>;
            }
            if (section.type === 'bullet-list' && section.items) {
              return (
                <ul key={idx}>
                  {section.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              );
            }
            if (section.type === 'faq' && section.qa) {
              return (
                <div key={idx} className="bg-secondary p-8 rounded-xl my-8 border border-border">
                  <h3 className="mt-0 text-2xl">FAQ</h3>
                  {section.qa.map((qa, i) => (
                    <div key={i} className="mb-6 last:mb-0">
                      <h4 className="font-bold text-lg mb-2">{qa.q}</h4>
                      <p className="m-0 text-muted-foreground">{qa.a}</p>
                    </div>
                  ))}
                </div>
              );
            }
            return null;
          })}
        </div>
        
        <div className="mt-16 pt-8 border-t border-border">
          <div className="bg-foreground text-white rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-display font-bold mb-4 text-white">{serviceLink?.heading ?? "Want to implement these strategies?"}</h2>
            <p className="text-white/80 mb-6">{serviceLink?.description ?? "Relevant Dealer can run this exact playbook for your operation."}</p>
            <Link href={serviceLink?.href ?? "/contact"} onClick={() => trackArticleCta(post.slug, serviceLink?.href ?? "/contact", "closing")} className="inline-flex bg-primary hover:bg-accent text-white font-bold px-8 py-4 rounded-md transition-colors">
              {serviceLink?.cta ?? "Talk to an Operator"}
            </Link>
            {serviceLink && (
              <p className="mt-5 text-sm text-white/90">
                {post.slug === "javascript-inventory-rendering-seo-acceptance-test" && <><Link href="/dealership-websites" onClick={() => trackArticleCta(post.slug, "/dealership-websites", "related_website")} className="underline underline-offset-4">Explore custom dealership websites</Link><span aria-hidden="true"> · </span></>}
                <Link href="/contact" onClick={() => trackArticleCta(post.slug, "/contact", "contact")} className="underline underline-offset-4">Already have a project in mind? Talk with our team.</Link>
              </p>
            )}
          </div>
        </div>
      </article>
    </div>
  );
}
